<script setup>
import { ref, computed, onUnmounted } from 'vue'
import { useNotificationsStore } from 'stores/notifications.js'

const props = defineProps({
  eventId: { type: [Number, String], required: true },
})

const store      = useNotificationsStore()
const show       = ref(false)
const card       = computed(() => store.eventCards[props.eventId])
const triggerRef = ref(null)
const pos        = ref({ verticalPos: 0, left: 0, above: true, arrowLeft: 20 })

let hideTimer = null
let showTimer = null

function reposition() {
  if (!triggerRef.value) return
  const rect          = triggerRef.value.getBoundingClientRect()
  const cardW         = 256
  const cardH         = 200
  const spaceAbove    = rect.top
  const spaceBelow    = window.innerHeight - rect.bottom
  const above         = spaceAbove >= cardH + 16 && spaceAbove >= spaceBelow
  const triggerCenter = rect.left + rect.width / 2
  const left          = Math.min(Math.max(8, triggerCenter - cardW / 2), window.innerWidth - cardW - 8)
  const arrowLeft     = Math.max(12, Math.min(Math.round(triggerCenter - left - 6), cardW - 24))
  pos.value = {
    left,
    verticalPos: above ? window.innerHeight - rect.top + 8 : rect.bottom + 8,
    above,
    arrowLeft,
  }
}

function onEnter() {
  clearTimeout(hideTimer)
  showTimer = setTimeout(() => {
    reposition()
    show.value = true
    store.fetchEventCard(props.eventId)
  }, 350)
}

function onLeave() {
  clearTimeout(showTimer)
  hideTimer = setTimeout(() => { show.value = false }, 180)
}

function onPopoverEnter() { clearTimeout(hideTimer) }
function onPopoverLeave() { hideTimer = setTimeout(() => { show.value = false }, 180) }

onUnmounted(() => { clearTimeout(hideTimer); clearTimeout(showTimer) })
</script>

<template>
  <span class="relative inline-block">
    <!-- Trigger event name — navigation handled by "View event" button in the popup -->
    <span ref="triggerRef"
          class="font-semibold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400
                 transition-colors cursor-default"
          @mouseenter="onEnter"
          @mouseleave="onLeave">
      <slot />
    </span>

    <Teleport to="body">
      <div v-if="show"
           :style="pos.above
             ? { position: 'fixed', left: pos.left + 'px', bottom: pos.verticalPos + 'px', zIndex: 9999, width: '256px' }
             : { position: 'fixed', left: pos.left + 'px', top: pos.verticalPos + 'px', zIndex: 9999, width: '256px' }"
           style="filter: drop-shadow(0 8px 24px rgba(0,0,0,0.14)) drop-shadow(0 2px 6px rgba(0,0,0,0.08))"
           @mouseenter="onPopoverEnter"
           @mouseleave="onPopoverLeave">

        <!-- Caret pointing down (card above trigger) -->
        <div v-if="pos.above"
             class="absolute -bottom-[6px] w-3 h-3 rotate-45
                    bg-white dark:bg-[#1C2128]
                    border-r border-b border-gray-300 dark:border-gray-500"
             :style="{ left: pos.arrowLeft + 'px' }" />

        <!-- Caret pointing up (card below trigger) -->
        <div v-else
             class="absolute -top-[6px] w-3 h-3 rotate-45
                    bg-white dark:bg-[#1C2128]
                    border-l border-t border-gray-300 dark:border-gray-500"
             :style="{ left: pos.arrowLeft + 'px' }" />

        <div class="w-full rounded-xl overflow-hidden
                    bg-white dark:bg-[#1C2128]
                    border border-gray-300 dark:border-gray-500">

          <!-- Skeleton -->
          <div v-if="!card || card.loading" class="p-4 animate-pulse">
            <div class="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-3" />
            <div class="space-y-2">
              <div class="h-2.5 bg-gray-100 dark:bg-gray-800 rounded w-full" />
              <div class="h-2.5 bg-gray-100 dark:bg-gray-800 rounded w-4/5" />
            </div>
            <div class="flex gap-3 mt-3">
              <div class="h-2.5 bg-gray-100 dark:bg-gray-800 rounded w-16" />
              <div class="h-2.5 bg-gray-100 dark:bg-gray-800 rounded w-20" />
            </div>
          </div>

          <!-- Error -->
          <div v-else-if="card.error"
               class="px-4 py-3 text-xs text-gray-400 dark:text-gray-500 text-center">
            Could not load event.
          </div>

          <!-- Loaded -->
          <div v-else-if="card.data">
            <div class="p-4">
              <!-- Event name with color dot -->
              <div class="flex items-center gap-2 mb-2">
                <span class="w-2.5 h-2.5 rounded-full flex-shrink-0" :class="card.data.color" />
                <p class="text-sm font-semibold text-gray-900 dark:text-white leading-tight">
                  {{ card.data.name }}
                </p>
              </div>

              <!-- Description -->
              <p v-if="card.data.description"
                 class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed line-clamp-3 mb-3">
                {{ card.data.description }}
              </p>

              <!-- Meta rows -->
              <div class="space-y-1.5">
                <div v-if="card.data.duration" class="flex items-center gap-2">
                  <svg class="w-3 h-3 fill-blue-500 flex-shrink-0" viewBox="0 0 512 512">
                    <path d="M256 0a256 256 0 1 1 0 512A256 256 0 1 1 256 0zM232 120V256c0 8 4 15.5 10.7 20l96 64c11 7.4 25.9 4.4 33.3-6.7s4.4-25.9-6.7-33.3L280 243.2V120c0-13.3-10.7-24-24-24s-24 10.7-24 24z"/>
                  </svg>
                  <span class="text-xs text-gray-600 dark:text-gray-300">{{ card.data.duration }}</span>
                </div>
                <div v-if="card.data.location" class="flex items-center gap-2">
                  <svg class="w-3 h-3 fill-blue-500 flex-shrink-0" viewBox="0 0 384 512">
                    <path d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z"/>
                  </svg>
                  <span class="text-xs text-gray-600 dark:text-gray-300">{{ card.data.location }}</span>
                </div>
              </div>
            </div>

            <!-- View event button -->
            <div class="px-4 pb-3 border-t border-gray-100 dark:border-gray-700 pt-3">
              <a :href="card.data.event_url"
                 target="_blank"
                 rel="noopener noreferrer"
                 class="flex items-center justify-center gap-1.5 w-full py-1.5 rounded-lg
                        text-xs font-semibold text-blue-600 dark:text-blue-400
                        ring-1 ring-blue-200 dark:ring-blue-800/60
                        hover:bg-blue-50 dark:hover:bg-blue-900/20
                        transition-colors no-underline">
                <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                </svg>
                View event
              </a>
            </div>
          </div>

        </div>
      </div>
    </Teleport>
  </span>
</template>
